import { lazy, Suspense, useEffect, useState } from 'react';
import {
  AppHeader,
  AppLauncher,
  EnvironmentBadge,
  ErrorBoundary,
  Footer,
  GlyphErrorPage,
  GlyphLoader,
  Loader,
  LanguageToggle,
  ThemeToggle,
  IconSprite,
  UserWidget,
  launcherApps,
} from '@weretradeit/ui-react';
import type { NavigationConfig } from '@weretradeit/ui-react';
import { useSession } from './lib/useSession';
import { ladeGlyphSzeneImLeerlauf } from './glyphScene';

const ChannelsPage = lazy(() => import('./pages/ChannelsPage'));
const ReleasePage = lazy(() => import('./pages/ReleasePage'));
const SettlementPage = lazy(() => import('./pages/SettlementPage'));

/* LADEN WIE IN JEDER APP DER FLOTTE (seit dem 02.10.2026).
   Hier stand `PageLoader` (eigener Kasten, `height: 50vh`, englischer Text)
   fuer drei verschiedene Lagen: Sitzung pruefen, zur Anmeldung umleiten,
   Seite nachladen. Jede App der Flotte wartete anders und an anderer Stelle.
   Der Vertrag des Design-Systems (FLEET-CONTRACT.md § 6, Regeln L3/L4/L6):
   Vollbild-Warten ist der erste Ladebildschirm, Warten im Inhalt
   `<Loader label>` — beides aus ui-react, mit deutscher Beschriftung.
   Seit 4.2.1 ist das Vollbild-Warten `GlyphLoader` mit der Szene dieser App
   statt des Kreisels `<Loader overlay="page">` — GLYPHFIELD.md § 3, „ein
   erster Ladebildschirm, der sonst nichts zu zeigen hat". Die Szene laedt
   der Effekt in `App` beim Einhaengen, im Leerlauf (glyphScene.ts); der
   Text steht als role="status" daneben.
   „Session wird geprüft" ist der Text der Nachbar-Apps (und ihrer
   E2E-Fixtures) — er bleibt wortgleich. */

const APP_KEY = 'sellerMeerkat';

/* Die Seite steht im Hash (`#/release`). Ein Pfad, den es hier nicht gibt,
   zeigte bis 4.2.1 still die Kanaele — jetzt die 404 der Flotte. Gelesen wird
   schon beim ersten Zeichnen, nicht erst im Effekt: sonst blitzte bei
   `#/release` einmal die Startseite auf. */
function currentRoute(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.hash.slice(1) || '/';
}

/* 3.x nimmt `navigation: { sections }` statt einer flachen `navItems`-Liste.
   Symbole wie in jeder anderen Menuezeile der Flotte; alle vier Namen stehen
   im Sprite (v3/icons/names.json, geprueft am 02.10.2026) — ein unbekannter
   Name rendert als leere Flaeche ohne Fehler. */
const NAVIGATION: NavigationConfig = {
  sections: [
    {
      items: [
        { label: 'Channels', to: '/', icon: 'network' },
        { label: 'Release', to: '/release', icon: 'upload' },
        { label: 'Settlements', to: '/settlements', icon: 'payments' },
        { label: 'Inbox', to: '/inbox', icon: 'inbox' },
      ],
    },
  ],
};

/* DIE ANWENDUNGSLISTE STEHT NICHT MEHR HIER (seit dem 02.10.2026).
   Hier stand eine eigene Liste mit fuenf Apps und kebab-Schluesseln
   (`seller-hub`, `accounting`, …). `AppLauncher` zeichnet jede Kachel als
   `<AppMark app={key}>`, und AppMark kennt nur die Namen des Design-Systems —
   jede Kachel zog `/marks/seller-hub.svg` usw. und blieb ein kaputtes Bild.
   Dazu fuehrte „Shop Beaver" auf den Storefront (shop.) statt auf den Admin
   (shopbeaver.), und „Service Ant" gibt es nicht mehr; mailBird und sieben
   weitere fehlten. `launcherApps('sellerMeerkat')` aus ui-react liest die
   Registry des Design-Systems (`v3/fleet-apps.json`) — dieselbe Liste in
   jeder App der Flotte. */

function Placeholder({ phase, title }: { phase: string; title: string }) {
  return (
    <div className="card">
      <div className="kicker">{phase}</div>
      <h2>{title}</h2>
      <p>Not built yet. See docs/PLAN.md for the delivery order.</p>
    </div>
  );
}

export default function App() {
  const [route, setRoute] = useState<string>(currentRoute);
  const { session, status } = useSession();

  /* Die GlyphField-Szene erst jetzt und erst im Leerlauf — nicht statisch aus
     main.tsx, dort haette die Engine schon gezeichnet, bevor die Huelle steht,
     und ihr den Hauptthread genommen (accountingFox PR 790). Ab hier zeichnet
     das Feld der Sitzungspruefung, waehrend die App auf das Netz wartet.
     Siehe glyphScene.ts. */
  useEffect(() => {
    ladeGlyphSzeneImLeerlauf();
  }, []);

  useEffect(() => {
    const onHashChange = () => setRoute(currentRoute());
    onHashChange();
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // authLion gates the whole app: unauthenticated users go to the shared login portal.
  // Beide Lagen sind derselbe erste Ladebildschirm (nichts sonst zu zeigen):
  // erst die Sitzungspruefung, dann — ohne Sitzung — die Umleitung.
  if (status === 'loading') return <GlyphLoader appKey={APP_KEY} label="Session wird geprüft…" />;
  if (status === 'anonymous') return <GlyphLoader appKey={APP_KEY} label="Weiterleitung zu Login…" />;

  const renderPage = () => {
    switch (route) {
      case '/release':
        return <ReleasePage />;
      case '/settlements':
        return <SettlementPage />;
      case '/inbox':
        return <Placeholder phase="Phase 4a" title="Unified inbox — eBay & BrickLink" />;
      case '/':
        return <ChannelsPage />;
      default:
        /* 4.2.1: die Fehlerseite der Flotte mit der eigenen Szene
           (GlyphErrorPage, Vorgabe kind="404" — die Szene ruht, nichts ist
           kaputt, die Adresse fehlt). Innerhalb der Shell: Kopfzeile und
           Menue bleiben stehen. „Zur Startseite" bleibt im Hash-Router. */
        return <GlyphErrorPage appKey={APP_KEY} homeHref="#/" />;
    }
  };

  /* Zwei Grenzen, beide mit `appKey` und OHNE eigenes `fallback` (ein
     `fallback` gewaenne in ErrorBoundary vor `appKey` und verdeckte die
     Fehlerseite): die innere um die Seite zeichnet einen Absturz als
     `GlyphErrorPage kind="500"` mit Kennung und „Erneut versuchen" INNERHALB
     der Shell; `key={route}` setzt sie beim Seitenwechsel zurueck. Die
     aeussere faengt, was in der Shell selbst bricht — bis 4.2.1 zeichnete
     sie dafuer die Karte mit Inline-Stilen. */
  return (
    <ErrorBoundary appKey={APP_KEY}>
      {/* Der Sprite MUSS im Dokument stehen: <Icon> loest ueber
          <use href="#i-…"> auf, und der Launcher-Knopf IST ein Icon. Fehlt er,
          bleibt der Knopf leer — ohne Fehler in der Konsole. */}
      <IconSprite src="/icons/sprite.svg" />
      {/* Kopfzeile in der Form von `FleetChrome` (ui-react 4.0.0), wie in
          jeder internen App der Flotte: Launcher · Sprache in `actions`,
          Thema in `themeSlot`, Umgebung + Nutzer in `userSlot`.

          Bis zum 02.10.2026 stand hier `<div id="wt-user-widget-mount" />` —
          eine Halterung fuer das vanille Widget, dessen Skript diese App nie
          geladen hat: rechts oben blieb es leer, kein Profil, kein Abmelden.
          Dazu haengte AppHeader ohne Slots eine ZWEITE Halterung an (doppelte
          id). Jetzt das Nutzermenue aus ui-react; es fragt `/me` beim selben
          Host wie `useSession` (login.weretrade.com, Begruendung dort) und
          meldet ueber `POST …/logout` ab. Stelle 5 im Cluster gibt
          app-shell.css erst ab 4.0.1 — bis dahin haelt index.css
          `.user-widget` dort.

          `appName` wie in der Registry: „SellerMeerkat", nicht der
          Schluessel. */}
      <AppHeader
        appName="SellerMeerkat"
        orgName="weretradeIT"
        mark="sellerMeerkat"
        navigation={NAVIGATION}
        activePath={route}
        actions={
          <>
            <AppLauncher apps={launcherApps('sellerMeerkat')} current="sellerMeerkat" />
            <LanguageToggle />
          </>
        }
        themeSlot={<ThemeToggle />}
        userSlot={
          <>
            <EnvironmentBadge />
            <UserWidget authBaseUrl="https://login.weretrade.com/api/auth" showQuickApps={false} />
          </>
        }
        brandingSlot={false}
        onNavigate={(item) => { if (item.to) window.location.hash = item.to; }}
      />
      {/* `app-container` definierte ui-react/styles.css; v3 nennt den
          Inhaltsrahmen `.page` im app-shell-Layout. */}
      <main className="app-main"><div className="page">
        <ErrorBoundary key={route} appKey={APP_KEY}>
          <Suspense fallback={<Loader size="lg" label="Seite wird geladen" />}>{renderPage()}</Suspense>
        </ErrorBoundary>
      </div></main>
      <Footer />
      <span hidden data-operator={session?.user?.username ?? ''} />
    </ErrorBoundary>
  );
}
